import * as React from 'react'

import type {
  IDrillDownFilterCategory,
  IDrillDownFilterContextValue,
} from './DrillDownFilter.types'

export interface IUseDrillDownFilterOptions<T> {
  categories: IDrillDownFilterCategory<T>[]
  initialSelection?: string[]
  initialCategoryId?: string
  onReset?: () => void
}

export default function useDrillDownFilter<T>({
  categories,
  initialSelection = [],
  initialCategoryId,
  onReset,
}: IUseDrillDownFilterOptions<T>): IDrillDownFilterContextValue<T> {
  const [selectedCategoryId, setSelectedCategoryId] = React.useState<
    string | null
  >(initialCategoryId ?? null)
  const [pendingSelection, setPendingSelection] = React.useState<Set<string>>(
    () => new Set(initialSelection),
  )
  const [treePath, setTreePath] = React.useState<string[]>([])
  const [searchTerm, setSearchTerm] = React.useState('')

  const selectedCategory = React.useMemo(
    () => categories.find(c => c.id === selectedCategoryId),
    [categories, selectedCategoryId],
  )

  const currentPathNodeId =
    treePath.length > 0 ? treePath[treePath.length - 1] : null

  const currentNode = React.useMemo(
    () =>
      currentPathNodeId && selectedCategory
        ? selectedCategory.items.find(
            item => selectedCategory.getItemId(item) === currentPathNodeId,
          )
        : undefined,
    [currentPathNodeId, selectedCategory],
  )

  const selectionByCategory = React.useMemo(() => {
    const map = new Map<string, { count: number; items: T[] }>()
    for (const category of categories) {
      const selectedItems = category.items.filter(item =>
        pendingSelection.has(category.getItemId(item)),
      )
      map.set(category.id, {
        count: selectedItems.length,
        items: selectedItems,
      })
    }
    return map
  }, [categories, pendingSelection])

  const onCategoryClick = React.useCallback((categoryId: string) => {
    setSelectedCategoryId(categoryId)
    setTreePath([])
    setSearchTerm('')
  }, [])

  const onBack = React.useCallback(() => {
    setSearchTerm('')
    if (treePath.length > 0) {
      setTreePath(prev => prev.slice(0, -1))
    } else {
      setSelectedCategoryId(null)
    }
  }, [treePath])

  const onDrillInto = React.useCallback((itemId: string) => {
    setSearchTerm('')
    setTreePath(prev => [...prev, itemId])
  }, [])

  const onToggleItem = React.useCallback((itemId: string) => {
    setPendingSelection(prev => {
      const next = new Set(prev)
      if (next.has(itemId)) {
        next.delete(itemId)
      } else {
        next.add(itemId)
      }
      return next
    })
  }, [])

  const onToggleItems = React.useCallback(
    (itemIds: string[], select: boolean) => {
      setPendingSelection(prev => {
        const next = new Set(prev)
        for (const id of itemIds) {
          if (select) {
            next.add(id)
          } else {
            next.delete(id)
          }
        }
        return next
      })
    },
    [],
  )

  const onClearAll = React.useCallback(() => {
    setPendingSelection(new Set())
    onReset?.()
  }, [onReset])

  const getSelectionCountForCategory = React.useCallback(
    (category: IDrillDownFilterCategory<T>): number =>
      selectionByCategory.get(category.id)?.count ?? 0,
    [selectionByCategory],
  )

  const getSelectedItemsForCategory = React.useCallback(
    (category: IDrillDownFilterCategory<T>): T[] =>
      selectionByCategory.get(category.id)?.items ?? [],
    [selectionByCategory],
  )

  const getSelectedIds = React.useCallback(
    () => Array.from(pendingSelection),
    [pendingSelection],
  )

  return React.useMemo(
    () => ({
      state: { selectedCategoryId, pendingSelection, treePath, searchTerm },
      actions: {
        onCategoryClick,
        onBack,
        onDrillInto,
        onToggleItem,
        onToggleItems,
        onClearAll,
        setSearchTerm,
        getSelectedIds,
      },
      meta: {
        categories,
        selectedCategory,
        currentPathNodeId,
        currentNode,
        getSelectionCountForCategory,
        getSelectedItemsForCategory,
      },
    }),
    [
      selectedCategoryId,
      pendingSelection,
      treePath,
      searchTerm,
      onCategoryClick,
      onBack,
      onDrillInto,
      onToggleItem,
      onToggleItems,
      onClearAll,
      setSearchTerm,
      getSelectedIds,
      categories,
      selectedCategory,
      currentPathNodeId,
      currentNode,
      getSelectionCountForCategory,
      getSelectedItemsForCategory,
    ],
  )
}
